import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/auth";
import { getAllProfiles } from "@/services/profileService";
import { getAllSubscribers } from "@/services/subscriptionService";
import { Subscriber } from "@/types/subscription";
import { debugLogger } from "@/utils/debug";

const normalizeSubscriptionSource = (value: string | null | undefined): Subscriber["subscription_source"] => {
  if (value === "admin" || value === "stripe") {
    return value;
  }

  if (value === "free" || value === "internal") {
    return "free";
  }

  return null;
};

export function useAdminSubscriptions() {
  const { isAdmin } = useAuth();

  return useQuery({
    queryKey: ["adminSubscriptions"],
    queryFn: async () => {
      if (!isAdmin) {
        throw new Error("Not authorized");
      }

      try {
        const profilesData = await getAllProfiles();
        const subscribersData = await getAllSubscribers();

        interface SubscriberRecord {
          id: string;
          email: string;
          user_id: string | null;
          subscribed: boolean;
          tier?: string | null;
          tier_code?: string | null;
          subscription_tier?: string | null;
          subscription_end?: string | null;
          expires_at?: string | null;
          subscription_source?: string | null;
          status?: string | null;
          starts_at?: string | null;
          cancel_at_period_end?: boolean;
          grace_period_ends_at?: string | null;
          scheduled_tier_code?: string | null;
          created_at: string;
          updated_at: string;
        }

        const subscriberMap: Record<string, SubscriberRecord> = {};

        subscribersData?.forEach((subscriber) => {
          if (subscriber.user_id) {
            subscriberMap[subscriber.user_id] = subscriber as SubscriberRecord;
          } else if (subscriber.email) {
            subscriberMap[`email:${subscriber.email.toLowerCase()}`] = subscriber as SubscriberRecord;
          }
        });

        return profilesData.map((profile) => {
          const subscriberRecord =
            subscriberMap[profile.id] ||
            (profile.email ? subscriberMap[`email:${profile.email.toLowerCase()}`] : null);

          if (subscriberRecord) {
            return {
              ...subscriberRecord,
              hasSubscriptionRecord: true,
              profile,
              email: profile.email || subscriberRecord.email,
              tier: subscriberRecord.tier_code || subscriberRecord.tier || subscriberRecord.subscription_tier,
              subscription_end: subscriberRecord.expires_at || subscriberRecord.subscription_end,
              subscription_source: normalizeSubscriptionSource(subscriberRecord.subscription_source),
            } as Subscriber;
          }

          return {
            id: `temp-${profile.id}`,
            user_id: profile.id,
            email: profile.email,
            hasSubscriptionRecord: false,
            subscribed: false,
            tier: "free_tier",
            subscription_source: "free",
            created_at: profile.created_at,
            updated_at: profile.created_at,
            profile,
          } as Subscriber;
        });
      } catch (error) {
        debugLogger.error("Error fetching subscriptions:", error);
        throw error;
      }
    },
    enabled: isAdmin,
  });
}
